import * as cornerstoneTools from "cornerstone-tools";
import { Event, EventData } from "cornerstone-tools";
import * as cornerstone from "cornerstone-core";
import { CornerstoneElement } from "cornerstone-core";
import * as cornerstoneMath from "cornerstone-math";
import { Region, RegionType } from "../../api/StudyApi";
import { HARD_POSITIVE_CONFIDENCE, NEGATIVE_CONFIDENCE, POSITIVE_CONFIDENCE } from "./RegionMenu";
import CornerstoneToolsWrapper from "../../util/CornerstoneToolsWrapper";
import { isInsideRectangle, Rectangle } from "../../util/RectangleUtility";
import { Point } from "./Point";

const HANDLE_TRIGGER_DISTANCE = 10;

export interface RegionToolElement extends CornerstoneElement {
  onChangeRegion?: (region: Region) => void;
  onOverRegion?: (region: Region, isIn: boolean, point?: Point) => void;
  updateRegions?: (regions: Region[]) => void;
  onHandling?: () => void;
}

const hasChangeRegion = (data: any) => {
  const region = data.region;

  if (!region) return false;

  return (
    region.x !== Math.min(data.handles.start.x, data.handles.end.x) ||
    region.y !== Math.min(data.handles.start.y, data.handles.end.y) ||
    region.width !== Math.abs(data.handles.end.x - data.handles.start.x) ||
    region.height !== Math.abs(data.handles.end.y - data.handles.start.y)
  );
};

const pageRelativePoint = (element: HTMLElement & RegionToolElement, region: Region): Point => {
  const elementRelative = cornerstone.pixelToCanvas(element, {
    x: region.x + region.width,
    y: region.y,
  });

  const rect = element.getBoundingClientRect();

  return {
    x: window.pageXOffset + rect.left + elementRelative.x,
    y: window.pageYOffset + rect.top + elementRelative.y,
  };
};

export default class RegionMouseTool extends CornerstoneToolsWrapper.BaseAnnotationToolClass<RegionToolElement> {
  // redefine type of parent element
  static NAME = "RegionMouse";

  constructor(props = {}) {
    const defaultProps = {
      name: RegionMouseTool.NAME,
      supportedInteractionTypes: ["Mouse"],
      configuration: {
        deleteIfHandleOutsideImage: false,
        preventHandleOutsideImage: false,
      },
    };

    super(props, defaultProps);
  }

  createNewMeasurement(eventData: EventData<RegionToolElement>) {
    const goodEventData = eventData && eventData.currentPoints && eventData.currentPoints.image;

    if (!goodEventData) {
      console.error(`required eventData not supplied to tool createNewMeasurement`);

      return;
    }

    return {
      visible: true,
      active: true,
      invalidated: true,
      handles: {
        start: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: false,
        },
        end: {
          x: eventData.currentPoints.image.x,
          y: eventData.currentPoints.image.y,
          highlight: true,
          active: true,
        },
      },
    };
  }

  pointNearTool(element: HTMLElement & RegionToolElement, data: any, coords: Point, interactionType?: any) {
    const hasStartAndEndHandles = data && data.handles && data.handles.start && data.handles.end;
    const validParameters = hasStartAndEndHandles;

    if (!validParameters) {
      console.warn(`invalid parameters supplied to tool pointNearTool`);
    }

    if (!validParameters || data.visible === false) {
      return false;
    }

    const distance = HANDLE_TRIGGER_DISTANCE;

    const rect = this.buildRectFromHandles(element, data.handles);

    if (isInsideRectangle(rect, coords)) {
      return true;
    }

    const distanceToPoint = cornerstoneMath.rect.distanceToPoint(rect, coords);

    return distanceToPoint < distance;
  }

  private buildRectFromHandles(element: RegionToolElement, handles: { start: Point; end: Point }): Rectangle {
    const startCanvas = cornerstone.pixelToCanvas(element, handles.start);
    const endCanvas = cornerstone.pixelToCanvas(element, handles.end);

    return {
      x: Math.min(startCanvas.x, endCanvas.x),
      y: Math.min(startCanvas.y, endCanvas.y),
      width: Math.abs(startCanvas.x - endCanvas.x),
      height: Math.abs(startCanvas.y - endCanvas.y),
    };
  }

  renderToolData(evt: Event) {
    const toolData = cornerstoneTools.getToolState(evt.currentTarget, RegionMouseTool.NAME);

    if (!toolData) {
      return;
    }

    const eventData = evt.detail;
    const { element } = eventData;
    const { handleRadius, drawHandlesOnHover } = this["configuration"];
    const context = CornerstoneToolsWrapper.getNewContext(eventData.canvasContext.canvas);

    CornerstoneToolsWrapper.draw(context, (context) => {
      // If we have tool data for this element - iterate over each set and draw it
      for (let i = 0; i < toolData.data.length; i++) {
        const data = toolData.data[i];

        if (data.visible === false) {
          continue;
        }

        // Check which color the rendered tool should be
        const region = data.region;

        const regionActive = region && region.active && region._selected;

        // Configure

        let color = cornerstoneTools.toolColors.getColorIfActive(data);

        if (data.region && data.region.badge) {
          color = region.badge.color;
        }

        const baseLineWidth = 2.5 * cornerstoneTools.toolStyle.getToolWidth();

        const strokeStyle = color;
        let lineWidth = regionActive ? 2 * baseLineWidth : baseLineWidth;
        let lineDash = null;

        // Adapt region border
        if (region) {
          if (regionActive) {
            context.globalAlpha = 1;
          } else if (region.attenuated) {
            context.globalAlpha = 0.3;
          } else {
            context.globalAlpha = 0.7;
          }

          if (region.confidence == HARD_POSITIVE_CONFIDENCE) {
            lineWidth *= 1.75;
          }

          if (
            region.confidence < POSITIVE_CONFIDENCE ||
            (region._type == RegionType.NEGATIVE && region.confidence == NEGATIVE_CONFIDENCE)
          ) {
            const fill = 10 * ((50 + region.confidence) / 150);
            const hole = 5;
            lineDash = [fill, hole];
          }

          if (!regionActive && region._selected) {
            lineWidth *= 2.5;
          }
        }

        // Draw
        CornerstoneToolsWrapper.drawRect(
          context,
          element,
          data.handles.start,
          data.handles.end,
          {
            color,
            strokeStyle,
            lineWidth,
            lineDash,
          },
          "pixel"
        );

        const handleOptions = {
          color,
          handleRadius,
          drawHandlesIfActive: drawHandlesOnHover,
        };

        CornerstoneToolsWrapper.drawHandles(context, eventData, data.handles, handleOptions);

        const handleStartCanvas = cornerstone.pixelToCanvas(element, data.handles.start);
        const handleEndCanvas = cornerstone.pixelToCanvas(element, data.handles.end);

        const leftCanvas = Math.min(handleStartCanvas.x, handleEndCanvas.x);
        const topCanvas = Math.min(handleStartCanvas.y, handleEndCanvas.y);
        const widthCanvas = Math.abs(handleStartCanvas.x - handleEndCanvas.x);

        if (region && region.badge) {
          context.beginPath();
          context.fillStyle = color;
          context.textAlign = "right";
          context.font = "18px Arial";
          context.fillText(region.badge.label, leftCanvas + widthCanvas, topCanvas - 5);
        }

        // Attach new region to data
        if (!data.region && !data.active) {
          if (typeof element.onChangeRegion === "function") {
            data.region = {
              x: Math.min(data.handles.start.x, data.handles.end.x),
              y: Math.min(data.handles.start.y, data.handles.end.y),
              width: Math.abs(data.handles.end.x - data.handles.start.x),
              height: Math.abs(data.handles.end.y - data.handles.start.y),
            };

            // New region call back
            console.debug("RegionTool - region added", data.region);
            element.onChangeRegion(data.region);
          } else {
            console.warn("No handler onChangeRegion");
          }
        }

        // Dispatch on change region
        if (data.invalidated && !data.active && hasChangeRegion(data)) {
          data.invalidated = false;

          data.region.x = Math.min(data.handles.start.x, data.handles.end.x);
          data.region.y = Math.min(data.handles.start.y, data.handles.end.y);
          data.region.width = Math.abs(data.handles.end.x - data.handles.start.x);
          data.region.height = Math.abs(data.handles.end.y - data.handles.start.y);

          console.debug("RegionTool - region updated", data.region);
          if (typeof element.onChangeRegion === "function") {
            element.onChangeRegion(data.region);
          }
        }
      }
    });
  }

  distanceFromPoint(element: HTMLElement & RegionToolElement, data: any, coords: any): any {
    console.error("distanceFromPoint must not be called");
  }

  preMouseDownCallback(evt: Event): boolean {
    const eventData = evt.detail;
    let data: any;
    const element = eventData.element;

    const coords = eventData.startPoints.canvas;
    const toolData = cornerstoneTools.getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return false;
    }

    let i;

    for (i = 0; i < toolData.data.length; i++) {
      data = toolData.data[i];

      if (
        this.pointNearTool(element, data, coords) ||
        CornerstoneToolsWrapper.getHandleNearImagePoint(element, data.handles, coords, HANDLE_TRIGGER_DISTANCE)
      ) {
        if (typeof element.onHandling === "function") {
          element.onHandling();
        }
        return false;
      }
    }

    return false;
  }

  toolSelectedCallback(evt: any, annotation: any, interactionType: any) {
    super.toolSelectedCallback(evt, annotation, interactionType);
  }

  mouseMoveCallback(evt: Event) {
    const eventData = evt.detail;
    cornerstoneTools.toolCoordinates.setCoords(eventData);

    // If we have no tool data for this element, do nothing
    const element = eventData.element;

    const toolData = cornerstoneTools.getToolState(evt.currentTarget, RegionMouseTool.NAME);

    if (!toolData) {
      return;
    }

    // We have tool data, search through all data
    // And see if we can activate a handle
    let imageNeedsUpdate = false;

    for (let i = 0; i < toolData.data.length; i++) {
      // Get the cursor position in canvas coordinates
      const coords = eventData.currentPoints.canvas;

      const data = toolData.data[i];

      if (CornerstoneToolsWrapper.handleActivator(element, data.handles, coords, HANDLE_TRIGGER_DISTANCE) === true) {
        imageNeedsUpdate = true;
      } else {
        if (
          (this.pointNearTool(element, data, coords) && !data.active) ||
          (!this.pointNearTool(element, data, coords) && data.active)
        ) {
          data.active = !data.active;
          imageNeedsUpdate = true;

          // Trigger over on I/O
          if (typeof element.onOverRegion === "function") {
            // Check that region is created and has coords
            if (data.region && data.region.x && data.region.y && data.region.width) {
              element.onOverRegion(data.region, data.active, pageRelativePoint(element, data.region));
            }
          }
        }
      }
    }

    // Handle activation status changed, redraw the image
    if (imageNeedsUpdate === true) {
      cornerstone.updateImage(element);
    }
  }

  updateRegions(regions: Region[]) {
    const element = this.element;

    cornerstoneTools.clearToolState(element, this.name);

    for (const reg of regions) {
      cornerstoneTools.addToolState(element, this.name, {
        region: reg,
        existing: true,
        color: reg.badge.color,
        visible: true,
        active: false,
        invalidated: true,
        handles: {
          start: {
            x: reg.x,
            y: reg.y,
            highlight: false,
            active: false,
          },
          end: {
            x: reg.x + reg.width,
            y: reg.y + reg.height,
            highlight: false,
            active: false,
          },
        },
      });
    }
  }

  registerOnChangeRegion(func: (region: Region) => void) {
    this.element.onChangeRegion = func;
  }

  registerOnOverRegion(func: (region: Region, isIn: boolean, p: Point) => void) {
    this.element.onOverRegion = func;
  }

  registerOnHandling(func: () => void) {
    this.element.onHandling = func;
  }
}
